import { useState, useEffect } from 'react';

/**
 * Determines whether an error is caused by network failure / offline status.
 * @param {Error|Object|string} error 
 * @returns {boolean}
 */
export function isNetworkError(error) {
    if (typeof window !== 'undefined' && !navigator.onLine) {
        return true;
    }

    if (!error) return false;

    const message = (
        typeof error === 'string'
            ? error
            : error.message || error.error_description || error.details || ''
    ).toLowerCase();

    const name = error.name || '';
    const status = error.status || error.code || 0;

    // Status 0 often indicates network failure or CORS blocking in fetch
    if (status === 0 || status === '0') return true;

    // Check common network error keywords
    const networkKeywords = [
        'failed to fetch',
        'networkerror',
        'network error',
        'net::err_',
        'connection_refused',
        'load failed',
        'timeout',
        'typeerror: failed to fetch',
        'internet connection',
        'offline',
        'unable to connect',
    ];

    if (networkKeywords.some(kw => message.includes(kw))) {
        return true;
    }

    if (name === 'TypeError' && message.includes('fetch')) {
        return true;
    }

    return false;
}

/**
 * Gets a user-friendly error message for auth and API failures.
 * @param {Error|Object} error 
 * @param {string} fallbackMsg 
 * @returns {string}
 */
export function getFriendlyErrorMessage(error, fallbackMsg = "An unexpected error occurred.") {
    if (isNetworkError(error)) {
        return "Unable to connect to network. Please check your internet connection and try again.";
    }

    if (typeof error === 'string') return error;
    if (error?.message) return error.message;

    return fallbackMsg;
}

/**
 * Custom React hook to monitor network status (online/offline).
 */
export function useNetworkStatus() {
    const [isOnline, setIsOnline] = useState(
        typeof navigator !== 'undefined' ? navigator.onLine : true
    );

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return isOnline;
}
